import DataTable from "react-data-table-component";
import styles from "./styles";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Api from "../../../../Api/Api";
import PropTypes from "prop-types";
import colors from "../../../../colors";
import CreateUser from "../CreateUser";
import Icon from "@mui/material/Icon";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Certificates from "../../profile/Certificates";
import { downloadCsvFile } from "../../../../utils/helper";
import azure from "../../../../utils/azure";
import NotifyUsers from "../NotifyUsers";
import userModel from "../../../../assets/data/user";


const SEARCHABLE_FIELDS = [
  "CompanyID",
  "FirstName",
  "LastName",
  "Email",
  "JobClassification",
  "JobName",
];

const EMPTY_EXTENDED_FILTERS = {
  company: "",
  jobClassification: "",
  jobName: "",
  certification: "",
};

const TEMP_PASSWORD_CACHE_TTL_MS = 60 * 60 * 1000;
const getTempPasswordCacheKey = (instance, userId) => `admin-temp-password:${instance}:${userId}`;
const readTempPasswordCache = (instance, userId) => {
  if (typeof sessionStorage === "undefined") return null;
  if (!instance || !userId) return null;
  try {
    const raw = sessionStorage.getItem(getTempPasswordCacheKey(instance, userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const expiresAtMs = parsed?.visibleUntil ? new Date(parsed.visibleUntil).getTime() : 0;
    if (!expiresAtMs || expiresAtMs <= Date.now()) {
      sessionStorage.removeItem(getTempPasswordCacheKey(instance, userId));
      return null;
    }
    return parsed;
  } catch (err) {
    return null;
  }
};
const writeTempPasswordCache = (instance, userId, payload) => {
  if (typeof sessionStorage === "undefined") return;
  if (!instance || !userId || !payload) return;
  sessionStorage.setItem(
    getTempPasswordCacheKey(instance, userId),
    JSON.stringify(payload)
  );
};

const normalizeText = (value) => (value || "").toString().toLowerCase().trim();
const normalizeForMatch = (value) =>
  normalizeText(value)
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getCertificationTitles = (status) => {
  if (!status || typeof status !== "object") return [];
  const sections = ["completed", "pending", "expiring", "expired"];
  const titles = new Set();

  sections.forEach((section) => {
    const certs = Array.isArray(status[section]) ? status[section] : [];
    certs.forEach((cert) => {
      if (!cert) return;
      const possibleTitles = [
        cert.title,
        cert.spanishTitle,
        cert.quiz,
        cert.quizName,
        cert.quizname,
        cert.videoName,
        cert.videoname,
      ];
      possibleTitles.forEach((title) => {
        if (title) titles.add(title);
      });
    });
  });

  return Array.from(titles);
};

const certificationMatchesQuery = (titles, query) => {
  const normalizedQuery = normalizeForMatch(query);
  if (!normalizedQuery) return true;

  const queryTokens = normalizedQuery.split(" ").filter(Boolean);
  if (!queryTokens.length) return true;

  return titles.some((title) => {
    const normalizedTitle = normalizeForMatch(title);
    if (!normalizedTitle) return false;

    if (normalizedTitle.includes(normalizedQuery) || normalizedQuery.includes(normalizedTitle)) {
      return true;
    }

    return queryTokens.every((token) => normalizedTitle.includes(token));
  });
};

const matchesBaseFilters = (item, searchTerm, filters) => {
  const matchesSearch = !searchTerm || SEARCHABLE_FIELDS.some((field) => {
    const value = normalizeText(item[field]);
    return value.includes(searchTerm);
  });

  if (!matchesSearch) return false;
  if (filters.company && item.CompanyID !== filters.company) return false;
  if (filters.jobClassification && item.JobClassification !== filters.jobClassification) return false;
  if (filters.jobName && item.JobName !== filters.jobName) return false;

  return true;
};

const ExpandedComponent = ({ data }) => {
  return <div style={styles.expandable}>
    <Certificates user={{ ...data, id: data.UserID }} />
  </div>;
};

ExpandedComponent.propTypes = {
  data: PropTypes.object.isRequired
};

function MecAdmin(props) {
  const [processing, setProcessing] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userData, setUserData] = useState([]);
  const [message, setMessage] = useState(false);
  const [terminated, setTerminated] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [showExtendedFilters, setShowExtendedFilters] = useState(false);
  const [extendedFilters, setExtendedFilters] = useState(EMPTY_EXTENDED_FILTERS);
  const [certificationStatusByUser, setCertificationStatusByUser] = useState({});
  const [appliedCertificationFilter, setAppliedCertificationFilter] = useState("");
  const [loadingCertFilters, setLoadingCertFilters] = useState(false);
  const [certificationFilterMessage, setCertificationFilterMessage] = useState("");
  const certSearchAbortRef = useRef(null);
  const certSearchRunRef = useRef(0);
  const { fetchCertificates } = azure;

  let defaultColumns = [
    {
      name: "Email",
      selector: row => row.Email,
      sortable: true
    },
    {
      name: "First Name",
      selector: row => row.FirstName,
      sortable: true
    },
    {
      name: "Last Name",
      selector: row => row.LastName,
      sortable: true
    },
    {
      name: "Job Classification",
      selector: row => row.JobClassification,
      sortable: true
    },
    {
      name: "Job Name",
      selector: row => row.JobName,
      sortable: true
    },
    {
      name: "Company",
      selector: row => row.CompanyID,
      sortable: true
    },
    {
      name: "Profile",
      selector: row => <button
        onClick={(e) => window.open("/profile?email=" + row.Email, '_blank')}
        style={styles.btn}
      >View</button>
    }
  ];
  const [columns, setColumns] = useState(defaultColumns);
  const [createUser, setCreateUser] = useState(false);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [tempPasswordModalUser, setTempPasswordModalUser] = useState(null);
  const [tempPasswordMode, setTempPasswordMode] = useState("manual");
  const [tempPasswordInput, setTempPasswordInput] = useState("");
  const [showGeneratedTempPassword, setShowGeneratedTempPassword] = useState(false);
  const [tempPasswordSubmitting, setTempPasswordSubmitting] = useState(false);
  const [tempPasswordError, setTempPasswordError] = useState("");
  const [tempPasswordResult, setTempPasswordResult] = useState(null);
  const [tempPasswordTick, setTempPasswordTick] = useState(Date.now());
  const appInstance = process.env.REACT_APP_APP_INSTANCE;
  const actorEmail = (props?.user?.email || props?.user?.Email || "").toString().toLowerCase();

  const getUsers = async (query = "") => {
    setLoadingUsers(true);
    try {
      const response = await Api.getAllUsers(query);
      const users = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.users)
          ? response.data.users
          : [];
      setUserData(users);
    } catch (err) {
      console.log(err);
      setUserData([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  const baseFilteredUsers = useMemo(() => {
    if (!Array.isArray(userData)) return [];
    const searchTerm = normalizeText(filterText);
    return userData.filter((item) => matchesBaseFilters(item, searchTerm, extendedFilters));
  }, [
    userData,
    filterText,
    extendedFilters.company,
    extendedFilters.jobClassification,
    extendedFilters.jobName
  ]);

  const stopCertificationSearch = useCallback((reason = "Certification search stopped. You can update filters and run again.") => {
    if (certSearchAbortRef.current) {
      certSearchAbortRef.current.abort();
      certSearchAbortRef.current = null;
    }
    certSearchRunRef.current += 1;
    setLoadingCertFilters(false);
    setCertificationFilterMessage(reason);
  }, []);

  const applyCertificationFilter = async () => {
    const query = normalizeText(extendedFilters.certification);
    if (!query) {
      setAppliedCertificationFilter("");
      setCertificationStatusByUser({});
      setCertificationFilterMessage("");
      return;
    }

    if (!baseFilteredUsers.length) {
      setAppliedCertificationFilter("");
      setCertificationStatusByUser({});
      setCertificationFilterMessage("No users match the current base filters. Adjust filters first.");
      return;
    }

    if (certSearchAbortRef.current) {
      certSearchAbortRef.current.abort();
    }
    const runId = certSearchRunRef.current + 1;
    certSearchRunRef.current = runId;
    const controller = new AbortController();
    certSearchAbortRef.current = controller;
    const isStaleRun = () => controller.signal.aborted || runId !== certSearchRunRef.current;

    setLoadingCertFilters(true);
    setCertificationFilterMessage(`Loading certifications for ${baseFilteredUsers.length} filtered users...`);

    try {
      const next = {};
      const batchSize = 10;

      for (let i = 0; i < baseFilteredUsers.length; i += batchSize) {
        if (isStaleRun()) throw new Error("CERT_SEARCH_ABORTED");
        const batch = baseFilteredUsers.slice(i, i + batchSize);
        const responses = await Promise.all(
          batch.map(async (user) => {
            if (!user?.Email) return null;
            try {
              const res = await Api.certificatesStatus(user.Email, {
                useCache: false,
                noCache: true,
                requestConfig: { signal: controller.signal }
              });
              const payload = res?.data && typeof res.data === "object" ? res.data : {};
              const status =
                payload[user.UserID] ||
                payload[String(user.UserID)] ||
                Object.values(payload)[0] ||
                null;

              if (!status) return null;
              return { userId: user.UserID, status };
            } catch (err) {
              const canceled =
                err?.code === "ERR_CANCELED" ||
                err?.name === "CanceledError" ||
                err?.name === "AbortError";
              if (canceled || isStaleRun()) throw err;
              console.log(err);
              return null;
            }
          })
        );

        responses.forEach((entry) => {
          if (entry?.userId) {
            next[entry.userId] = entry.status;
          }
        });
      }
      if (isStaleRun()) throw new Error("CERT_SEARCH_ABORTED");

      const matchedCount = baseFilteredUsers.filter((user) => {
        const userStatus = next[user.UserID] || next[String(user.UserID)] || null;
        const certTitles = getCertificationTitles(userStatus);
        return certificationMatchesQuery(certTitles, query);
      }).length;

      setCertificationStatusByUser(next);
      setAppliedCertificationFilter(query);
      setCertificationFilterMessage(
        `Certification filter applied on ${baseFilteredUsers.length} filtered users. ${matchedCount} matched "${extendedFilters.certification}".`
      );
    } catch (err) {
      const canceled =
        err?.code === "ERR_CANCELED" ||
        err?.name === "CanceledError" ||
        err?.name === "AbortError" ||
        err?.message === "CERT_SEARCH_ABORTED" ||
        runId !== certSearchRunRef.current ||
        controller.signal.aborted;

      if (canceled) {
        if (runId === certSearchRunRef.current) {
          setCertificationFilterMessage("Certification search stopped. You can update filters and run again.");
        }
        return;
      }
      console.log(err);
      setCertificationStatusByUser({});
      setAppliedCertificationFilter("");
      setCertificationFilterMessage("Unable to load certifications. Please try again.");
    } finally {
      if (runId === certSearchRunRef.current) {
        setLoadingCertFilters(false);
      }
      if (certSearchAbortRef.current === controller) {
        certSearchAbortRef.current = null;
      }
    }
  };

  const onCertificationKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      applyCertificationFilter();
    }
  };

  useEffect(() => {
    if (loadingCertFilters && certSearchAbortRef.current) {
      certSearchAbortRef.current.abort();
      certSearchAbortRef.current = null;
      certSearchRunRef.current += 1;
      setLoadingCertFilters(false);
      setCertificationFilterMessage("Certification search stopped because base filters changed.");
    }

    if (!appliedCertificationFilter) return;
    setAppliedCertificationFilter("");
    setCertificationStatusByUser({});
    setCertificationFilterMessage(
      "Base filters changed. Press Enter or Apply to run certification search on the new filtered user set."
    );
  }, [
    filterText,
    extendedFilters.company,
    extendedFilters.jobClassification,
    extendedFilters.jobName
  ]);

  const terminateUser = (e, user) => {
    if (terminated) {
      alert(`Are you sure you want to delete ${user.Email}? Once this is done the user cannot be recovered. A new account will have to be made for this email.`);
      Api.delete(user.UserID).then(done => getUsers("?terminated=yes")).catch(err => console.log(err));
    } else {
      alert(`Are you sure you want to terminate ${user.Email}?`);
      Api.terminate(user.UserID).then(done => getUsers()).catch(err => console.log(err));
    }
  };

  const reinstateUser = (e, user) => {
    alert(`Are you sure you want to reinstate ${user.Email}?`);
    Api.reinstate(user.UserID).then(done => {
      getUsers();
      setTerminated(false);
    }).catch(err => console.log(err));
  };

  const showTerminated = (bool) => {
    setTerminated(bool);
    if (bool) getUsers("?terminated=yes");
    else getUsers();
  };

  const setAdmin = (e, user) => {
    Api.makeAdmin(user.UserID).then(done => {
      getUsers();
    }).catch(err => console.log(err));
  };

  const clearLocked = (e, user) => {
    Api.reset_locked(user.Email).then(done => {
      setMessage(`Reset of ${user.Email} login attempts done`);
      getUsers();
      setTimeout(() => setMessage(false), 5000);
    }).catch(err => console.log(err));
  };

  const closeTempPasswordModal = () => {
    setTempPasswordModalUser(null);
    setTempPasswordMode("manual");
    setTempPasswordInput("");
    setShowGeneratedTempPassword(false);
    setTempPasswordError("");
    setTempPasswordSubmitting(false);
  };

  const openTempPasswordModal = (user) => {
    setTempPasswordModalUser(user);
    setTempPasswordMode("manual");
    setTempPasswordInput("");
    setShowGeneratedTempPassword(false);
    setTempPasswordError("");
    const cached = readTempPasswordCache(appInstance, user?.UserID);
    setTempPasswordResult(cached);
  };

  const handleTempPasswordCreate = async () => {
    if (!tempPasswordModalUser?.UserID) return;
    if (!actorEmail) {
      setTempPasswordError("Unable to identify admin account email from the current session.");
      return;
    }

    const shouldGenerate = tempPasswordMode === "generate";
    if (!shouldGenerate && !tempPasswordInput.trim()) {
      setTempPasswordError("Enter a temporary password or switch to Generate.");
      return;
    }

    setTempPasswordSubmitting(true);
    setTempPasswordError("");

    try {
      const response = await Api.adminCreateTempPassword({
        targetUserId: tempPasswordModalUser.UserID,
        tempPassword: shouldGenerate ? "" : tempPasswordInput.trim(),
        generate: shouldGenerate,
        adminEmail: actorEmail
      });
      const result = response?.data || {};
      if (!result?.success || !result?.tempPassword) {
        throw new Error("Temporary password was not returned.");
      }

      const visibleUntil = new Date(Date.now() + TEMP_PASSWORD_CACHE_TTL_MS).toISOString();
      const nextResult = {
        tempPassword: result.tempPassword,
        expiresAt: result.expiresAt,
        visibleUntil,
        targetEmail: tempPasswordModalUser.Email
      };
      writeTempPasswordCache(appInstance, tempPasswordModalUser.UserID, nextResult);
      setTempPasswordResult(nextResult);
      setTempPasswordInput("");
      setShowGeneratedTempPassword(true);
    } catch (err) {
      const passwordErrors = err?.response?.data?.password;
      if (Array.isArray(passwordErrors)) {
        setTempPasswordError(passwordErrors.join(", "));
      } else {
        setTempPasswordError(
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to create temporary password."
        );
      }
    } finally {
      setTempPasswordSubmitting(false);
    }
  };

  const copyTempPassword = async () => {
    if (!tempPasswordResult?.tempPassword) return;
    try {
      await navigator.clipboard.writeText(tempPasswordResult.tempPassword);
      setMessage("Temporary password copied to clipboard.");
      setTimeout(() => setMessage(false), 5000);
    } catch (err) {
      setTempPasswordError("Unable to copy automatically. Please copy manually.");
    }
  };

  useEffect(() => {
    if (!tempPasswordModalUser || !tempPasswordResult?.visibleUntil) return undefined;
    const interval = setInterval(() => setTempPasswordTick(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [tempPasswordModalUser, tempPasswordResult?.visibleUntil]);

  useEffect(() => {
    if (!tempPasswordModalUser?.UserID || !tempPasswordResult?.visibleUntil) return;
    const visibleUntilMs = new Date(tempPasswordResult.visibleUntil).getTime();
    if (!visibleUntilMs || visibleUntilMs > tempPasswordTick) return;
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(getTempPasswordCacheKey(appInstance, tempPasswordModalUser.UserID));
    }
    setTempPasswordResult(null);
  }, [tempPasswordTick, tempPasswordModalUser, tempPasswordResult, appInstance]);

  const tempPasswordVisibleSeconds = useMemo(() => {
    if (!tempPasswordResult?.visibleUntil) return 0;
    const diff = Math.floor((new Date(tempPasswordResult.visibleUntil).getTime() - tempPasswordTick) / 1000);
    return diff > 0 ? diff : 0;
  }, [tempPasswordResult?.visibleUntil, tempPasswordTick]);

  useEffect(() => {
    let newColumns = [...defaultColumns];
    if (terminated) newColumns.push({
      name: "Reinstate",
      selector: row => <button
        name="reinstate"
        style={{ ...styles.btn, backgroundColor: "transparent", color: colors.MARATHON.primary }}
        onClick={e => reinstateUser(e, row)}
      >
        <Icon fontSize="medium">person_add</Icon>
      </button>
    });
    else newColumns = newColumns.filter(c => c.name !== "Reinstate");

    if (props.type === "Admin+") {
      newColumns.push({
        name: "Make Admin",
        selector: row => <button
          name="Make Admin"
          style={{ ...styles.btn, backgroundColor: "transparent", color: colors.MARATHON.primary }}
          onClick={e => row.Admin && (row.Admin === "Admin" || row.Admin === "Admin+") ? null : setAdmin(e, row)}
        >
          {row.Admin && (row.Admin === "Admin" || row.Admin === "Admin+") ? row.Admin :
            <Icon fontSize="medium">admin_panel_settings</Icon>}
        </button>
      });
      newColumns.push({
        name: terminated ? "Archive" : "Terminate",
        selector: row => <button
          name="remove"
          style={{ ...styles.btn, backgroundColor: "transparent", color: colors.MARATHON.primary }}
          onClick={e => terminateUser(e, row)}
        >
          <Icon fontSize="medium">{terminated ? "delete" : "person_remove"}</Icon>
        </button>
      });
      newColumns.push({
        name: "Clear Login Attempts",
        selector: row => <button
          name="Clear Login Attempts"
          style={{ ...styles.btn, backgroundColor: "transparent", color: colors.MARATHON.primary }}
          onClick={e => clearLocked(e, row)}
        >
          <Icon fontSize="medium">lock_reset</Icon>
        </button>
      });
      newColumns.push({
        name: "Temp Password",
        selector: row => <button
          name="Temporary Password"
          style={{ ...styles.btn, backgroundColor: "transparent", color: colors.MARATHON.primary }}
          onClick={() => openTempPasswordModal(row)}
        >
          <Icon fontSize="medium">vpn_key</Icon>
        </button>
      });
    }

    setColumns(newColumns);
  }, [terminated, props.type]);

  const companyOptions = useMemo(() => {
    const instance = process.env.REACT_APP_APP_INSTANCE;
    const defaults = userModel.companyId[instance] || [];
    const options = new Set(defaults);
    userData.forEach((user) => {
      if (user?.CompanyID) options.add(user.CompanyID);
    });
    return Array.from(options).sort((a, b) => a.localeCompare(b));
  }, [userData]);

  const jobClassificationOptions = useMemo(() => {
    const options = new Set(userModel.classification || []);
    userData.forEach((user) => {
      if (user?.JobClassification) options.add(user.JobClassification);
    });
    return Array.from(options).sort((a, b) => a.localeCompare(b));
  }, [userData]);

  const jobNameOptions = useMemo(() => {
    const options = new Set();
    userData.forEach((user) => {
      if (user?.JobName) options.add(user.JobName);
    });
    return Array.from(options).sort((a, b) => a.localeCompare(b));
  }, [userData]);

  const certificationOptions = useMemo(() => {
    const options = new Set();
    Object.values(certificationStatusByUser || {}).forEach((status) => {
      getCertificationTitles(status).forEach((title) => options.add(title));
    });
    return Array.from(options).sort((a, b) => a.localeCompare(b));
  }, [certificationStatusByUser]);

  const filteredItems = useMemo(() => {
    if (!Array.isArray(baseFilteredUsers)) return [];
    const selectedCertification = normalizeText(appliedCertificationFilter);

    if (!selectedCertification) return baseFilteredUsers;

    return baseFilteredUsers.filter((item) => {
      if (selectedCertification) {
        const userStatus =
          certificationStatusByUser[item.UserID] ||
          certificationStatusByUser[String(item.UserID)] ||
          null;

        const certTitles = getCertificationTitles(userStatus);
        if (!certificationMatchesQuery(certTitles, selectedCertification)) return false;
      }

      return true;
    });
  }, [baseFilteredUsers, certificationStatusByUser, appliedCertificationFilter]);

  const exportUsers = async (e) => {
    if (filteredItems) {
      setProcessing(true);
      const filename = "users.csv";
      const data = ["Training Topic, Classification, Name, Email, Date of Completion, Date of Expiration, Job Name, Company"];
      for (let user in filteredItems) {
        const userID = filteredItems[user].UserID;
        let userRow = {
          topic: "",
          classification: filteredItems[user].JobClassification,
          name: filteredItems[user].FirstName + " " + filteredItems[user].LastName,
          email: filteredItems[user].Email,
          dateOfCompletion: "",
          expiration: "",
          jobName: filteredItems[user].JobName,
          companyName: filteredItems[user].CompanyID
        };
        const certificates = await fetchCertificates(userID).then(certs => certs).catch(err => console.log(err));
        if (certificates.length > 0) {
          for (let c in certificates) {
            let certificate = certificates[c];
            if (certificate.metadata) {
              userRow.topic = certificate.metadata.quizName || certificate.metadata.quizname || certificate.metadata.videoName || certificate.metadata.videoname || "";
              userRow.dateOfCompletion = certificate.metadata.issued || "";
              data.push(`${userRow.topic}, ${userRow.classification}, ${userRow.name}, ${userRow.email}, ${userRow.dateOfCompletion}, ${userRow.expiration}, ${userRow.jobName}, ${userRow.companyName}`);
            }
          }
        } else data.push(`${userRow.topic}, ${userRow.classification}, ${userRow.name}, ${userRow.email}, ${userRow.dateOfCompletion}, ${userRow.expiration}, ${userRow.jobName}, ${userRow.companyName}`);
        data.push("");
      }
      downloadCsvFile(
        data.join("\n"),
        filename
      );
      setProcessing(false);
    }
  };

  const updateExtendedFilter = (field, value) => {
    setExtendedFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearAllFilters = () => {
    if (loadingCertFilters || certSearchAbortRef.current) {
      stopCertificationSearch("Certification search stopped while clearing filters.");
    }
    setFilterText("");
    setExtendedFilters(EMPTY_EXTENDED_FILTERS);
    setAppliedCertificationFilter("");
    setCertificationStatusByUser({});
    setCertificationFilterMessage("");
    setResetPaginationToggle(!resetPaginationToggle);
  };

  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <div style={styles.toolbarWrapper}>
        <div style={styles.toolbar}>
          <div style={styles.toolbarGroup}>
            <input
              style={styles.input}
              autoComplete="off"
              placeholder="Search Users"
              onChange={e => setFilterText(e.target.value)}
              value={filterText}
            />
            <button
              style={{ ...styles.btn, backgroundColor: colors.Theme.lightGray, color: colors.Theme.black }}
              onClick={(e) => showTerminated(!terminated)}
            >{terminated ? "Hide" : "Show"} Terminated Users</button>
            {!createUser && <button
              style={{ ...styles.btn, backgroundColor: colors.Theme.successLight }}
              onClick={(e) => setCreateUser(true)}
            >Add User</button>}
            <button
              style={{ ...styles.btn, backgroundColor: colors.Theme.blue }}
              onClick={() => setShowExtendedFilters(!showExtendedFilters)}
            >{showExtendedFilters ? "Hide Extended View" : "Extended View"}</button>
          </div>
          <div style={styles.toolbarGroup}>
            <button
              style={{ ...styles.btn, backgroundColor: colors.Theme.black }}
              onClick={clearAllFilters}
            >Clear Filters</button>
            <button
              onClick={exportUsers}
              style={{ ...styles.btn, backgroundColor: colors.Theme.blue }}
            >{processing ? "...Processing" : "Export"}</button>
          </div>
        </div>

        {showExtendedFilters && (
          <div style={styles.filterPanel}>
            <label style={styles.filterField}>
              <span style={styles.filterLabel}>Company</span>
              <select
                style={styles.select}
                value={extendedFilters.company}
                onChange={(e) => updateExtendedFilter("company", e.target.value)}
              >
                <option value="">All Companies</option>
                {companyOptions.map((company) => (
                  <option key={company} value={company}>{company}</option>
                ))}
              </select>
            </label>

            <label style={styles.filterField}>
              <span style={styles.filterLabel}>Job Classification</span>
              <select
                style={styles.select}
                value={extendedFilters.jobClassification}
                onChange={(e) => updateExtendedFilter("jobClassification", e.target.value)}
              >
                <option value="">All Job Classifications</option>
                {jobClassificationOptions.map((classification) => (
                  <option key={classification} value={classification}>{classification}</option>
                ))}
              </select>
            </label>

            <label style={styles.filterField}>
              <span style={styles.filterLabel}>Job Name</span>
              <select
                style={styles.select}
                value={extendedFilters.jobName}
                onChange={(e) => updateExtendedFilter("jobName", e.target.value)}
              >
                <option value="">All Job Names</option>
                {jobNameOptions.map((jobName) => (
                  <option key={jobName} value={jobName}>{jobName}</option>
                ))}
              </select>
            </label>

            <label style={styles.filterField}>
              <span style={styles.filterLabel}>Certification</span>
              <input
                style={styles.select}
                list="certification-options"
                value={extendedFilters.certification}
                placeholder={loadingCertFilters ? "Loading Certifications..." : "Type certification and press Enter"}
                onChange={(e) => updateExtendedFilter("certification", e.target.value)}
                onKeyDown={onCertificationKeyDown}
              />
              <datalist id="certification-options">
                {certificationOptions.map((certification) => (
                  <option key={certification} value={certification} />
                ))}
              </datalist>
              <div style={styles.filterActionRow}>
                <button
                  style={{ ...styles.btn, backgroundColor: colors.Theme.blue }}
                  disabled={loadingCertFilters}
                  onClick={applyCertificationFilter}
                >{loadingCertFilters ? "Loading..." : "Apply Certification Filter"}</button>
                {loadingCertFilters && (
                  <button
                    style={{ ...styles.btn, backgroundColor: "#9b1c1c" }}
                    onClick={() => stopCertificationSearch("Certification search stopped. You can start a new search now.")}
                  >Stop</button>
                )}
                {!!appliedCertificationFilter && (
                  <button
                    style={{ ...styles.btn, backgroundColor: colors.Theme.lightGray, color: colors.Theme.black }}
                    onClick={() => {
                      setAppliedCertificationFilter("");
                      setCertificationFilterMessage("");
                    }}
                  >Clear Certification Filter</button>
                )}
              </div>
              <p style={styles.filterHint}>
                {certificationFilterMessage || "Certification search runs against all users in your current base filters, not just the visible table page."}
              </p>
            </label>
          </div>
        )}
      </div>
    );
  }, [
    filterText,
    resetPaginationToggle,
    createUser,
    terminated,
    processing,
    showExtendedFilters,
    extendedFilters,
    baseFilteredUsers,
    companyOptions,
    jobClassificationOptions,
    jobNameOptions,
    certificationOptions,
    loadingCertFilters,
    appliedCertificationFilter,
    certificationFilterMessage,
    stopCertificationSearch
  ]);

  const userCreated = () => {
    setCreateUser(false);
    getUsers().then(done => done).catch(err => console.log(err));
  };

  return (
    <div style={styles.container}>
      {message && <p style={{ fontSize: 13 }}>{message}</p>}
      {createUser && <CreateUser cancel={() => setCreateUser(false)} onSubmit={userCreated} />}
      {tempPasswordModalUser && (
        <Modal
          open={Boolean(tempPasswordModalUser)}
          onClose={closeTempPasswordModal}
          aria-labelledby="temp-password-modal-title"
          aria-describedby="temp-password-modal-desc"
        >
          <Box sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 560,
            maxWidth: "96%",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 22, 66, 0.2)",
            borderRadius: "8px",
            boxShadow: 24,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 1.25
          }}>
            <h3 id="temp-password-modal-title" style={{ margin: 0 }}>
              Temporary Password
            </h3>
            <p id="temp-password-modal-desc" style={{ margin: 0 }}>
              {tempPasswordModalUser.FirstName} {tempPasswordModalUser.LastName} ({tempPasswordModalUser.Email})
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
              <button
                style={{
                  ...styles.btn,
                  backgroundColor: tempPasswordMode === "manual" ? colors.Theme.blue : colors.Theme.lightGray,
                  color: tempPasswordMode === "manual" ? "#fff" : colors.Theme.black
                }}
                onClick={() => setTempPasswordMode("manual")}
              >
                Manual
              </button>
              <button
                style={{
                  ...styles.btn,
                  backgroundColor: tempPasswordMode === "generate" ? colors.Theme.blue : colors.Theme.lightGray,
                  color: tempPasswordMode === "generate" ? "#fff" : colors.Theme.black
                }}
                onClick={() => setTempPasswordMode("generate")}
              >
                Generate
              </button>
            </div>

            {tempPasswordMode === "manual" && (
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Temporary Password</span>
                <input
                  style={styles.input}
                  value={tempPasswordInput}
                  onChange={(e) => setTempPasswordInput(e.target.value)}
                  placeholder="Enter temporary password"
                />
              </label>
            )}

            {!!tempPasswordError && (
              <p style={{ margin: 0, color: "#9b1c1c", fontSize: 13 }}>{tempPasswordError}</p>
            )}

            {tempPasswordResult?.tempPassword && (
              <div style={{
                border: "1px solid rgba(0, 22, 66, 0.18)",
                borderRadius: 6,
                padding: 10,
                background: "rgb(246, 249, 250)"
              }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 600 }}>
                  Temporary password visible in this browser for {Math.floor(tempPasswordVisibleSeconds / 60)}m {tempPasswordVisibleSeconds % 60}s
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                  <code style={{ fontSize: 16, wordBreak: "break-all" }}>
                    {showGeneratedTempPassword ? tempPasswordResult.tempPassword : "**********"}
                  </code>
                  <button
                    style={{ ...styles.btn, backgroundColor: colors.Theme.lightGray, color: colors.Theme.black }}
                    onClick={() => setShowGeneratedTempPassword(!showGeneratedTempPassword)}
                  >
                    {showGeneratedTempPassword ? "Hide" : "Show"}
                  </button>
                  <button
                    style={{ ...styles.btn, backgroundColor: colors.Theme.blue }}
                    onClick={copyTempPassword}
                  >
                    Copy
                  </button>
                </div>
                {tempPasswordResult.expiresAt && (
                  <p style={{ margin: "8px 0 0 0", fontSize: 12 }}>
                    User login with this temporary password expires: {new Date(tempPasswordResult.expiresAt).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
              <button
                style={{ ...styles.btn, backgroundColor: colors.Theme.lightGray, color: colors.Theme.black }}
                onClick={closeTempPasswordModal}
              >
                Close
              </button>
              <button
                style={{ ...styles.btn, backgroundColor: colors.Theme.blue }}
                onClick={handleTempPasswordCreate}
                disabled={tempPasswordSubmitting}
              >
                {tempPasswordSubmitting ? "Creating..." : "Create Temporary Password"}
              </button>
            </div>
          </Box>
        </Modal>
      )}
      {!loadingUsers && <DataTable
        title="Users"
        columns={columns}
        data={filteredItems}
        expandableRows
        pagination
        paginationResetDefaultPage={resetPaginationToggle}
        subHeader
        subHeaderComponent={subHeaderComponentMemo}
        highlightOnHover
        pointerOnHover
        expandableRowsComponent={ExpandedComponent}
      />}
      <NotifyUsers />
    </div>
  );
}

MecAdmin.propTypes = {
  type: PropTypes.string,
  user: PropTypes.object
};

export default MecAdmin;
